import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Paper, Fab } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import GridLayout from 'react-grid-layout';
import isEqual from 'lodash.isequal';
import { Loader, Flex } from '~/shared/components';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import { useSorting } from '~/shared/hooks';
import { useNavAndFilters, useScroller, WidgetOverridesContext, WidgetOverridesMap } from '~/shared/services/charts';
import { toStringArray } from '~/shared/utils/array';
import { downloadGridData } from '~/shared/services/api';
import FilterBar, { NarrativeSelection } from './FilterBar';
import {
  getCascadingHierarchyIds,
  findNodeDescription,
  findNodeLabel,
  resolveCascadingSelectionFromCandidates,
  selectionToFilterParams,
} from '~/shared/utils/cascadingNarrativeFilters';
import { pinCompanyFilterParamsForWidget } from '~/shared/utils/omitCompanyFilterParams';
import {
  DEFAULT_MMP_TARGET_DASHBOARD_ID,
  findNavPathById,
  isMmpPortfolioTableWidget,
  replaceLastPathSegment,
  shouldNavigateMmpCompanyClick,
} from '~/shared/utils/navDashboardPath';
import HeatmapPage from './HeatmapPage';
import HorizontalBarChartPage from './HorizontalBarChartPage';
import TableChartPage from './TableChartPage';
import HeadlessTableChartPage from './HeadlessTableChartPage';
import StackedBarChartPage from './StackedBarChartPage';
import StackedAreaChartPage from './StackedAreaChartPage';
import HorizontalStackedBarChartPage from './HorizontalStackedBarChartPage';
import HorizontalPosNegStackedBarChartPage from './HorizontalPosNegfStackedBarChartPage';
import DoubleYAxisLineChartPage from './DoubleYAxisLineChartPage';
import YAxisLineChartPage from './YAxisLineChartPage';
import TornadoChartPage from './TornadoChartPage';
import TornadoPosNegChartPage from './TornadoPosNegChartPage';
import ScatterChartPage from './ScatterChartPage';
import VideoPlayerPage from './VideoPlayerPage';
import ImagePlayerPage from './ImagePlayerPage';
import WordCloudChartPage from './WordCloudChartPage';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorDoubleLineChart from './Indicator/IndicatorDoubleLineChart';
import IndicatorTable from './Indicator/IndicatorTable';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import { defaultFilter } from './defaultIndicator';
import { searchToObj } from '~/shared/utils';
import { useSearchParams } from '~/shared/hooks';
import { fetchNavHirarchy } from '~/shared/services/api';
import { queryOptions } from '~/shared/constants';
import type { TopicExplainabilityExternalState } from './topicExplainability.types';

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  padding: 93px 0 0 0;
  .MuiFormControl-root {
    justify-content: center;
  }
`;

const Header = styled(Flex)`
  position: absolute;
  z-index: 2;
  left: 20px;
  top: -75px;
  color: var(--color-chart-header);
  font-size: 20px;
  font-weight: 500;
  width: 100%;
  align-items: center;
  padding: 0 0 0 14px;
  min-height: 83px;
  overflow: visible;
`;

const TitleBox = styled(Flex)`
  margin-left: 20px;
  margin-bottom: -6px;
  width: 100%;
  align-items: baseline;
  justify-content: flex-start;
  padding: 10px 0 0 14px;
  min-height: 35px;
  overflow: visible;
`;

const TitleBoxStart = styled(Flex)`
  color: var(--color-chart-header);
  font-size: 24px;
  font-weight: 400;
  align-items: baseline;
  min-height: 35px;
  overflow: visible;
`;

const TitleBoxAssetName = styled(Flex)`
  color: var(--color-chart-header);
  font-size: 24px;
  font-weight: 500;
  align-items: baseline;
  padding: 0 8px;
  min-height: 35px;
  overflow: visible;
`;

const TitleBoxAsset = styled(Flex)`
  color: var(--color-chart-header);
  font-size: 24px;
  font-weight: 500;
  align-items: baseline;
  padding: 0px 8px 0px 8px;
  min-height: 35px;
  overflow: visible;
`;

const TitleBoxEnd = styled(Flex)`
  color: var(--color-chart-header);
  font-size: 24px;
  font-weight: 400;
  align-items: baseline;
  min-height: 35px;
  overflow: visible;
`;

const ELEMENT_WRAPPER_PADDING = 12;

const ElementWrapper = styled.div`
  box-shadow:
    0px 2px 1px -1px rgb(0 0 0 / 20%),
    0px 1px 1px 0px rgb(0 0 0 / 14%),
    0px 1px 3px 0px rgb(0 0 0 / 12%);
  background: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: ${ELEMENT_WRAPPER_PADDING}px !important;
  margin: 0 !important;
`;

const SlotSizer = styled.div<{ $minheight: number }>`
  position: relative;
  width: 100%;
  min-height: ${({ $minheight }) => `${Math.max($minheight, 0)}px`};
  height: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  > * {
    flex: 1 1 auto;
    width: 100%;
    overflow: hidden;
  }
  > * .MuiPaper-root {
    overflow-y: hidden;
  }
`;

const IndicatorsContainer = styled.div`
  padding-left: 23px;
  padding-right: 23px;
  margin-top: -15px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const IndicatorContainer = styled.div<{ $iswide: number }>`
  padding-bottom: 23px;
  min-width: ${({ $iswide }) => ($iswide === 1 ? '100%' : 'calc(50% - 11px)')};
  max-width: ${({ $iswide }) => ($iswide === 1 ? '100%' : 'calc(50% - 11px)')};
`;

const INITIAL_TOPIC_EXPLAINABILITY_STATE: TopicExplainabilityExternalState = {
  topic: '',
  phase: 'idle',
  loading: false,
  error: null,
  data: null,
  destinationTitle: null,
  sourceTitle: null,
  requestId: 0,
};

const MainContainer = styled(Paper)`
  position: relative;
  max-width: calc(100% - 20px);
  margin-left: -4px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  background: transparent !important;
  box-shadow: none !important;
`;

const ContentContainer = styled.div`
  display: flex;
  padding: 4px;
`;

const FabContainer = styled.div`
  position: fixed;
  bottom: 15px;
  right: 15px;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: white;
  }
`;

const AlignedLoader = styled(Loader)`
  left: calc(50% - 25px);
  top: calc(50% - 25px);
  transform: none;
`;

const Container = styled.div<{ $hasChips: boolean }>`
  width: 100%;
  display: flex;
  padding-top: ${({ $hasChips }) => ($hasChips ? '30px' : '0')};
  > div {
    padding: 0 !important;
    margin: 0 !important;
    > div {
      padding: 0 !important;
      margin: 0 !important;
      > div {
        padding: 0 !important;
        margin: 0 !important;
        > div {
          padding: 0 !important;
          margin: 0 !important;
          > div {
            padding: 0 !important;
            margin: 0 !important;
            > div {
              padding: 0 !important;
              margin: 0 !important;
              > div {
                padding: 0 !important;
                margin: 0 !important;
                > div {
                  padding: 0;
                  margin: 0;
                }
              }
            }
          }
        }
      }
    }
  }
  .tooltip {
    padding: 6px 8px !important;
  }
  .react-draggable-transparent-selection {
    display: none !important;
  }
  .react-grid-item {
    box-shadow: none !important;
    border: 1px solid #e6e9eb;
  }
  .react-grid-item > div {
    overflow: hidden;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
  }
  #tableChart {
    padding: 18px 24px 0 24px !important;
  }
  .MuiPaper-elevation1 {
    box-shadow: none !important;
  }
  .MuiPaper-root {
    height: 100% !important;
    min-height: 100% !important;
    max-height: 100% !important;
  }
`;

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
};

export default function GridPage({ navHirarchy, isUserExpired }: any) {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setSelectedChartDisplay] = useDynamicRecoil('selectedChartDisplay', undefined);

  const [gridIndicator, setGridIndicator] = useDynamicRecoil('gridIndicator', undefined);

  const { scrollerRef, scrollToTop, scrollToBottom, offset } = useScroller();

  const { sorting, onSortingChange, initialSorting } = useSorting();

  const size = useWindowSize();

  const [open, setOpen] = useState(true);
  const [width, setWidth] = useState(size.width - 268);

  const [windowWidth, setWindowWidth] = useState(size.width - 268);
  const [windowSize, setWindowSize] = useState('');

  const handleSetOpen = useCallback(() => {
    setWidth(size.width - 268);
    setOpen(!open);
  }, [size, open]);

  const { category, subcategory, subsubcategory, prism, navHirarchyItem, prismId, filterValues, filterValuesID }: any =
    useNavAndFilters(navHirarchy);

  const queryStringParams = useMemo(() => searchToObj(location.search), [location]);
  const formationPeriod = useMemo(
    () =>
      queryStringParams['Formation Period']?.includes('Day')
        ? queryStringParams['Formation Period']
        : navHirarchyItem?.filters?.find((f: any) => f.id === 'Formation Period')?.defaultValue,
    [queryStringParams, navHirarchyItem],
  );

  const isShowTitle = useMemo(() => navHirarchyItem.title && navHirarchyItem.showTitle, [navHirarchyItem]);

  useEffect(() => {
    setSelectedChartDisplay(navHirarchyItem?.displayName || navHirarchyItem?.name);
  }, [navHirarchyItem]);

  const { gridElements } = navHirarchyItem;

  const onDownload = () => {
    downloadGridData(category, subcategory, subsubcategory, prism, filterValuesID, prismId).catch(e => {
      alert(e.message);
    });
  };

  const layoutLg = useMemo(() => gridElements.map((e: any) => e.gridDataLg), [gridElements]);
  const layoutMd = useMemo(() => gridElements.map((e: any) => e.gridDataMd), [gridElements]);
  const layoutSm = useMemo(() => gridElements.map((e: any) => e.gridDataSm), [gridElements]);

  const [layout, setLayout] = useState<any>({});
  const [grid, setGrid] = useState(layoutLg);

  useEffect(() => {
    if (size.width > 1599) {
      setWindowSize('lg');
      setLayout(layoutLg);
      setGrid('gridDataLg');
    }
    if (size.width > 999 && size.width < 1600) {
      setWindowSize('md');
      setLayout(layoutMd);
      setGrid('gridDataMd');
    }
    if (size.width < 1000) {
      setWindowSize('sm');
      setLayout(layoutSm);
      setGrid('gridDataSm');
    }
    setWindowWidth(size.width);
    setWidth(size.width - 268);
  }, [size, open, windowSize, setWindowSize, layoutLg, layoutMd, layoutSm, gridElements]);

  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  const [gridCategory, setGridCategory] = useState<any>();
  const [gridPrism, setGridPrism] = useState<any>();
  const [gridPrismId, setGridPrismId] = useState<any>();
  const [gridFilterValues, setGridFilterValues] = useState<any>();
  const [hasNarrativeChips, setHasNarrativeChips] = useState(false);

  const [hideYaxisLinechart, setHideYaxisLinechart] = useState<boolean>(false);
  const [widgetOverrides, setWidgetOverrides] = useState<WidgetOverridesMap>({});

  const [topicExplainabilityState, setTopicExplainabilityState] = useState<TopicExplainabilityExternalState>(
    INITIAL_TOPIC_EXPLAINABILITY_STATE,
  );

  const handleTopicExplainabilityStateChange = useCallback((next: TopicExplainabilityExternalState) => {
    setTopicExplainabilityState(next);
  }, []);

  const handleNarrativeSelectionChange = useCallback((selections: NarrativeSelection[]) => {
    setHasNarrativeChips(selections.length > 1);
  }, []);

  const columns = useMemo(() => {
    if (windowSize === 'lg') return 12;
    if (windowSize === 'md') return 8;
    return 4;
  }, [windowSize]);

  const isValidLayoutEntry = useCallback(
    (entry?: { h?: number; w?: number }) =>
      !!entry &&
      typeof entry.h === 'number' &&
      Number.isFinite(entry.h) &&
      entry.h > 0 &&
      typeof entry.w === 'number' &&
      Number.isFinite(entry.w) &&
      entry.w > 0,
    [],
  );

  const lastValidLayoutRef = useRef<Record<number, any>>({});

  useEffect(() => {
    if (!Array.isArray(layout)) {
      return;
    }

    const next: Record<number, any> = { ...lastValidLayoutRef.current };
    layout.forEach((entry: any, index: number) => {
      if (isValidLayoutEntry(entry)) {
        next[index] = { ...entry };
      }
    });
    lastValidLayoutRef.current = next;
  }, [isValidLayoutEntry, layout]);

  const baseLayoutArray = useMemo(() => {
    if (!Array.isArray(layout)) return [] as any[];

    return layout.map((item: any, index: number) => {
      const fallback = lastValidLayoutRef.current[index];
      if (!isValidLayoutEntry(item) && isValidLayoutEntry(fallback)) {
        return {
          ...fallback,
          gridHeight: fallback.h,
          gridWidth: fallback.w,
        };
      }

      const resolvedHeight = item?.h;
      const resolvedWidth = item?.w;

      if (!isValidLayoutEntry(item)) {
        return item;
      }

      return {
        ...item,
        y: item?.y ?? 0,
        h: resolvedHeight,
        w: resolvedWidth,
        gridHeight: resolvedHeight,
        gridWidth: resolvedWidth,
      };
    });
  }, [isValidLayoutEntry, layout]);

  useEffect(() => {
    if (gridIndicator && isEqual(indicatorSections, [])) setIndicatorSections(gridIndicator);
  }, [gridIndicator]);

  useEffect(() => {
    if (prism !== 'Customized Baskets') {
      setGridFilterValues(undefined);
      setIndicatorSections([]);
      setGridIndicator([]);
      setHideYaxisLinechart(true);
      setTimeout(() => setHideYaxisLinechart(false), 10);
      return;
    }
    setGridFilterValues({ ...defaultFilter, ...queryStringParams });
    // TODO check why we change prismId
    setGridPrismId('BasketsHeatMap');
    setHideYaxisLinechart(true);
    setTimeout(() => setHideYaxisLinechart(false), 10);
  }, [prism, queryStringParams, formationPeriod]);

  const { setParams } = useSearchParams();

  const { data: navHierarchy } = useQuery(fetchNavHirarchy.key, fetchNavHirarchy, queryOptions);
  const searchData = navHierarchy?.searchData || [];

  const normalizeOverrideCandidate = useCallback((rawValue: any) => {
    if (rawValue === null || rawValue === undefined) return undefined;
    if (rawValue instanceof Date) {
      const iso = rawValue.toISOString();
      return { value: iso, id: iso };
    }
    if (typeof rawValue !== 'object' || Array.isArray(rawValue)) {
      return { value: rawValue, id: rawValue };
    }

    const candidate = rawValue as Record<string, any>;
    const textLike = candidate.text ?? candidate.name ?? candidate.label ?? candidate.displayName;
    const valueLike = candidate.value ?? candidate.id ?? textLike;

    const resolvedValue =
      typeof textLike === 'string' || typeof textLike === 'number'
        ? textLike
        : typeof valueLike === 'string' || typeof valueLike === 'number'
          ? valueLike
          : undefined;
    const resolvedId =
      typeof candidate.id === 'string' || typeof candidate.id === 'number'
        ? candidate.id
        : typeof valueLike === 'string' || typeof valueLike === 'number'
          ? valueLike
          : typeof resolvedValue === 'string' || typeof resolvedValue === 'number'
            ? resolvedValue
            : undefined;

    if (resolvedValue === undefined && resolvedId === undefined) {
      return undefined;
    }

    const valueToUse = resolvedValue ?? resolvedId;
    const idToUse = resolvedId ?? resolvedValue;

    return { value: valueToUse, id: idToUse };
  }, []);

  const extractOverrideValue = useCallback((paramName: string, cellInfo: Record<string, any>) => {
    if (!paramName) return undefined;
    if (cellInfo[paramName] !== undefined) return cellInfo[paramName];
    const lowerParam = paramName.toLowerCase();
    const matchedKey = Object.keys(cellInfo).find(key => key.toLowerCase() === lowerParam);
    if (matchedKey !== undefined) {
      const value = cellInfo[matchedKey];
      if (value !== undefined) return value;
    }
    if ((lowerParam === 'date' || lowerParam === 'topic' || lowerParam === 'row') && cellInfo.topic !== undefined) {
      return cellInfo.topic;
    }
    if ((lowerParam === 'symbol' || lowerParam === 'column') && cellInfo.symbol !== undefined) {
      return cellInfo.symbol;
    }
    if (lowerParam === 'value' && cellInfo.value !== undefined) {
      return cellInfo.value;
    }
    return undefined;
  }, []);

  const onClick = useCallback(
    (cellInfo: {
      source: string;
      topic?: string;
      symbol?: string;
      value?: string;
      navHirarchyItem: any;
      category: string;
      prism: string;
      prismId: string;
      filterValues: any;
      filterValuesID?: any;
      indicatorSections: any;
    }) => {
      if (isUserExpired) return;
      const affectedWidgets = toStringArray(cellInfo?.navHirarchyItem?.affectedWidgets);
      const affectingParams = toStringArray(cellInfo?.navHirarchyItem?.affectingParams);
      const mergedOverrideValues: Record<string, any> = {};
      let hasMergedOverrideValues = false;

      if (affectedWidgets.length > 0) {
        setWidgetOverrides(prev => {
          const next = { ...prev };
          affectedWidgets.forEach(widgetId => {
            if (!widgetId) return;
            const overrideValues: Record<string, any> = {};
            const overrideIds: Record<string, any> = {};
            affectingParams.forEach(paramName => {
              const rawValue = extractOverrideValue(paramName, cellInfo);
              const normalized = normalizeOverrideCandidate(rawValue);
              if (!normalized) return;
              const { value: normalizedValue, id: normalizedId } = normalized;
              if (normalizedValue === undefined && normalizedId === undefined) return;
              const idToUse = normalizedId ?? normalizedValue;
              const valueToUse = normalizedValue ?? normalizedId;
              if (valueToUse === undefined || idToUse === undefined) return;
              overrideValues[paramName] = valueToUse;
              overrideIds[paramName] = idToUse;
            });
            if (Object.keys(overrideValues).length > 0) {
              next[widgetId] = { values: overrideValues, ids: overrideIds };
              if (Object.keys(overrideValues).length > 0) {
                Object.assign(mergedOverrideValues, overrideValues);
                hasMergedOverrideValues = true;
              }
            } else {
              delete next[widgetId];
            }
          });
          return next;
        });
      }
      const filterIndicator = cellInfo?.navHirarchyItem?.indicators?.[cellInfo?.source]?.[0];
      // Widget items carry a copy of the dashboard hierarchy; fall back to page-level tree.
      const tree = cellInfo?.navHirarchyItem?.narrativeFilterTree ?? navHirarchyItem?.narrativeFilterTree;
      const widgetId = cellInfo?.navHirarchyItem?.id;
      const widgetName = cellInfo?.navHirarchyItem?.name ?? cellInfo?.navHirarchyItem?.heatmap?.title;
      const isMmpTable = isMmpPortfolioTableWidget({ id: widgetId, name: widgetName });
      const hasCompanyHierarchy = Boolean(
        tree?.filterHierarchy?.some((level: { id?: string }) => String(level?.id || '').toLowerCase() === 'company'),
      );
      // SARTable / MMP may have empty related-data in cached nav; still treat row clicks as Company actions.
      const isCompanyFilterClick =
        filterIndicator?.[1] === 'filter' ||
        filterIndicator?.[1] === 'navigate' ||
        isMmpTable ||
        (hasCompanyHierarchy && Boolean((cellInfo as any)?.topicCandidates?.length || cellInfo?.topic));

      if (isCompanyFilterClick && (hasCompanyHierarchy || isMmpTable)) {
        const filterBy = filterIndicator?.[0] || 'company';
        const filterValue =
          cellInfo.source === 'value' ? cellInfo?.topic : (cellInfo as unknown as any)?.[cellInfo.source];
        const candidates = [
          filterValue,
          (cellInfo as any)?.topicName,
          (cellInfo as any)?.cellData,
          (cellInfo as any)?.name,
          ...(((cellInfo as any)?.topicCandidates as string[] | undefined) ?? []),
        ].filter((value, index, all) => value != null && value !== '' && all.indexOf(value) === index);

        const applyCompanySelection = (ids: Record<string, string>) => {
          const explicitTarget =
            typeof filterIndicator?.[2] === 'string' && filterIndicator[2] ? filterIndicator[2] : undefined;
          const wantsNavigate = shouldNavigateMmpCompanyClick({
            pagePrismId: prismId,
            pageName: navHirarchyItem?.name,
            widgetId,
            widgetName,
            actionType: filterIndicator?.[1],
            targetDashboardId: explicitTarget,
          });
          const targetDashboardId = wantsNavigate ? explicitTarget || DEFAULT_MMP_TARGET_DASHBOARD_ID : undefined;

          if (targetDashboardId) {
            const currentPath = (() => {
              try {
                return decodeURIComponent(location.pathname);
              } catch {
                return location.pathname;
              }
            })();
            // Prefer sibling swap (SingleAssetRepresentation2 → CompanyPage) over tree walk;
            // flattened nav can yield incomplete/wrong paths.
            const siblingPath = replaceLastPathSegment(location.pathname, targetDashboardId);
            const treePath = findNavPathById(navHirarchy, targetDashboardId);
            const resolvedPath = siblingPath && siblingPath !== currentPath ? siblingPath : treePath || siblingPath;

            if (resolvedPath && resolvedPath !== currentPath) {
              const search = new URLSearchParams(
                Object.entries(ids).reduce(
                  (acc, [key, value]) => {
                    if (value != null && value !== '') acc[key] = String(value);
                    return acc;
                  },
                  {} as Record<string, string>,
                ),
              ).toString();
              navigate({ pathname: resolvedPath, search: search ? `?${search}` : '' });
              return;
            }
          }

          // No link / same dashboard / unresolved path → keep today's same-page filter behavior.
          setParams({ newParams: ids });
        };

        if (tree?.filterHierarchy?.length) {
          const urlCompanyType =
            typeof window !== 'undefined'
              ? new URLSearchParams(window.location.search).get('company-type') || undefined
              : undefined;
          const preferRootId =
            urlCompanyType ||
            tree.defaultSelection?.['company-type'] ||
            (/public/i.test(`${window.location.pathname} ${document.title}`)
              ? 'public'
              : /private/i.test(`${window.location.pathname} ${document.title}`)
                ? 'private'
                : undefined);
          const selection = resolveCascadingSelectionFromCandidates(tree, candidates, preferRootId);
          if (selection) {
            const { ids } = selectionToFilterParams(tree, selection);
            applyCompanySelection(ids);
            return;
          }
          // Matching failed — still navigate when we have a target + a click label.
          const fallbackValue = candidates[0];
          const wantsNavigate = shouldNavigateMmpCompanyClick({
            pagePrismId: prismId,
            pageName: navHirarchyItem?.name,
            widgetId,
            widgetName,
            actionType: filterIndicator?.[1],
            targetDashboardId:
              typeof filterIndicator?.[2] === 'string' && filterIndicator[2] ? filterIndicator[2] : undefined,
          });
          if (fallbackValue != null && wantsNavigate) {
            applyCompanySelection({
              ...(preferRootId ? { 'company-type': preferRootId } : {}),
              [filterBy]: String(fallbackValue),
            });
            return;
          }
          return;
        }

        if (!filterBy || candidates[0] === undefined) return;
        applyCompanySelection({ [filterBy]: String(candidates[0]) });
        return;
      }
      setIndicatorSections([]);
      setGridIndicator([]);
      setGridCategory(cellInfo?.category);
      setGridPrism(cellInfo?.prism);
      setGridPrismId(cellInfo?.prismId);
      const nextFilterValues = { ...(cellInfo?.filterValues ?? {}) };
      if (hasMergedOverrideValues) {
        Object.assign(nextFilterValues, mergedOverrideValues);
      }
      setGridFilterValues(nextFilterValues);
      setTimeout(() => {
        const sections =
          cellInfo?.navHirarchyItem &&
          cellInfo?.filterValues &&
          cellInfo?.navHirarchyItem?.indicators?.[cellInfo?.source]?.map((indicatorObj: any) => {
            const indicator = indicatorObj && indicatorObj[0];
            const type = indicatorObj && indicatorObj[1];
            return { indicator, cellInfo, type };
          });
        cellInfo?.navHirarchyItem && cellInfo?.filterValues && setIndicatorSections(sections);
        sections.length > 0 && setTimeout(scrollToBottom, 400);
      }, 100);
      setTimeout(
        () =>
          cellInfo?.navHirarchyItem &&
          cellInfo?.filterValues &&
          setGridIndicator(
            cellInfo?.navHirarchyItem?.indicators?.[cellInfo?.source]?.map((indicatorObj: any) => {
              const indicator = indicatorObj && indicatorObj[0];
              const type = indicatorObj && indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        120,
      );
    },
    [
      scrollToBottom,
      isUserExpired,
      extractOverrideValue,
      setWidgetOverrides,
      normalizeOverrideCandidate,
      setParams,
      navHirarchyItem,
      navHirarchy,
      navigate,
      location.pathname,
      prismId,
    ],
  );

  const onFabClick = useCallback(() => {
    setTimeout(scrollToTop, 100);
  }, [scrollToTop]);

  useEffect(() => {
    if (prism !== 'Customized Baskets') {
      // TODO check why we change prismId
      setGridPrismId('BasketsHeatMap');
    }

    setTimeout(scrollToTop, 100);
  }, [navHirarchyItem, filterValues, scrollToTop, prism]);

  const [refreshFilterBar, setRefreshFilterBar] = useState(false);

  useEffect(() => {
    setRefreshFilterBar(true);
    setTimeout(() => setRefreshFilterBar(false), 10);
  }, [location?.pathname]);

  const type = useMemo(() => navHirarchyItem?.type, [navHirarchyItem]);
  const narrativeFilterTree = useMemo(() => navHirarchyItem?.narrativeFilterTree, [navHirarchyItem]);
  const filters = useMemo(() => navHirarchyItem?.filters, [navHirarchyItem]);
  const cascadingHierarchyIds = useMemo(
    () => (narrativeFilterTree ? getCascadingHierarchyIds(narrativeFilterTree) : new Set<string>()),
    [narrativeFilterTree],
  );
  const filtersForBar = useMemo(() => {
    if (!narrativeFilterTree || !Array.isArray(filters)) {
      return filters;
    }
    return filters.filter(
      (filter: { id?: string; name?: string }) =>
        !cascadingHierarchyIds.has(String(filter?.id ?? '')) && !cascadingHierarchyIds.has(String(filter?.name ?? '')),
    );
  }, [cascadingHierarchyIds, filters, narrativeFilterTree]);
  const dateType = useMemo(() => filtersForBar?.[filtersForBar?.length - 1 || 0]?.type, [filtersForBar]);

  const gridHorizontalGap = 23;
  const gridVerticalGap = 23;
  const rowHeightPx = 100;

  const children = useMemo(() => {
    const slotPaddingOffset = ELEMENT_WRAPPER_PADDING * 2;

    return gridElements.map((e: any, i: number) => {
      const widgetId = e?.item?.id ?? e?.item?.widget_id ?? e?.item?.widgetId;
      const widgetOverride = widgetId ? widgetOverrides?.[widgetId] : undefined;

      const mergedFilterValues = widgetOverride?.values ? { ...filterValues, ...widgetOverride.values } : filterValues;

      const mergedFilterValuesID = widgetOverride?.ids ? { ...filterValuesID, ...widgetOverride.ids } : filterValuesID;

      // Temporary: SARTable keeps stable default company params so Company clicks don't refetch it.
      // (Omitting company entirely 500s; BE still requires those params.)
      const resolvedGridFilterValues =
        pinCompanyFilterParamsForWidget(mergedFilterValues, widgetId, narrativeFilterTree, 'labels') ??
        mergedFilterValues;
      const resolvedGridFilterValuesID =
        pinCompanyFilterParamsForWidget(mergedFilterValuesID, widgetId, narrativeFilterTree, 'ids') ??
        mergedFilterValuesID;

      const layoutEntryFromGrid = layout?.[i];
      const fallbackEntry = lastValidLayoutRef.current[i];
      const hasValidGridLayout = isValidLayoutEntry(layoutEntryFromGrid);
      const hasValidFallback = isValidLayoutEntry(fallbackEntry);
      const effectiveLayoutEntry = hasValidGridLayout
        ? layoutEntryFromGrid
        : hasValidFallback
          ? {
              ...(layoutEntryFromGrid ?? fallbackEntry ?? {}),
              h: fallbackEntry.h,
              w: fallbackEntry.w,
            }
          : null;

      const effectiveGridHeight = effectiveLayoutEntry?.h;
      const effectiveGridWidth = effectiveLayoutEntry?.w;

      const safeHeightUnits =
        typeof effectiveGridHeight === 'number' && effectiveGridHeight > 0 ? effectiveGridHeight : 1;
      const slotHeightPx = rowHeightPx * safeHeightUnits;
      const slotMinHeightPx = Math.max(slotHeightPx - slotPaddingOffset, 0);

      const gridEntry = e?.[grid as unknown as string];
      const elementKey = gridEntry?.i ?? layoutEntryFromGrid?.i ?? fallbackEntry?.i ?? widgetId ?? i;

      if (!gridEntry || gridEntry.i === undefined || !effectiveLayoutEntry) {
        return (
          <ElementWrapper key={String(elementKey)}>
            <SlotSizer $minheight={slotMinHeightPx}>
              <AlignedLoader />
            </SlotSizer>
          </ElementWrapper>
        );
      }

      const slotContent =
        e.item?.type === 'bar' ? (
          <StackedBarChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'table' ? (
          <TableChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
            topicExplainabilityState={topicExplainabilityState}
            onTopicExplainabilityStateChange={handleTopicExplainabilityStateChange}
          />
        ) : e.item?.type === 'headless-table' ? (
          <HeadlessTableChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
          />
        ) : e.item?.type === 'horizontalBar' ? (
          <HorizontalBarChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'wordCloud' ? (
          <WordCloudChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'horizontalStackedBar' ? (
          <HorizontalStackedBarChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'horizontalPosNegStackedBar' ? (
          <HorizontalPosNegStackedBarChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            gridWidth={effectiveGridWidth}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'tornado' ? (
          <TornadoChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'tornadoPosNeg' ? (
          <TornadoPosNegChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : e.item?.type === 'scatter' ? (
          <ScatterChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
          />
        ) : e.item?.type === 'heatmap' ? (
          <HeatmapPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
            gridSorting={sorting}
            onGridSortingChange={onSortingChange}
            gridInitialSorting={initialSorting}
          />
        ) : e.item?.type === 'gif' ? (
          <VideoPlayerPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
          />
        ) : e.item?.type === 'png' ? (
          <ImagePlayerPage navHirarchy={navHirarchy} gridIndex={i} />
        ) : e.item?.type === 'double-y-axis' ? (
          <DoubleYAxisLineChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
            onTopicExplainabilityStateChange={handleTopicExplainabilityStateChange}
          />
        ) : e.item?.type === 'y-axis' && !hideYaxisLinechart ? (
          <YAxisLineChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
            onTopicExplainabilityStateChange={handleTopicExplainabilityStateChange}
          />
        ) : e.item?.type === 'y-axis' && hideYaxisLinechart ? (
          <AlignedLoader />
        ) : e.item?.type === 'area' ? (
          <StackedAreaChartPage
            navHirarchy={navHirarchy}
            gridIndex={i}
            gridHeight={effectiveGridHeight}
            onGridClick={onClick}
            gridFilterValues={resolvedGridFilterValues}
            gridFilterValuesID={resolvedGridFilterValuesID}
            gridPrism={gridPrism}
          />
        ) : (
          <AlignedLoader />
        );

      return (
        <ElementWrapper key={String(elementKey)}>
          <SlotSizer $minheight={slotMinHeightPx}>{slotContent}</SlotSizer>
        </ElementWrapper>
      );
    });
  }, [
    filterValues,
    filterValuesID,
    grid,
    gridElements,
    gridPrism,
    hideYaxisLinechart,
    isValidLayoutEntry,
    layout,
    narrativeFilterTree,
    navHirarchy,
    onSortingChange,
    onClick,
    topicExplainabilityState,
    handleTopicExplainabilityStateChange,
    widgetOverrides,
    sorting,
    initialSorting,
    rowHeightPx,
  ]);

  const titleText = String(navHirarchyItem?.title || '');
  const companyFilterValue =
    filterValuesID?.company ?? filterValues?.company ?? filterValuesID?.Company ?? filterValues?.Company ?? null;
  const companyTypeFilterValue =
    filterValuesID?.['company-type'] ??
    filterValues?.['company-type'] ??
    filterValuesID?.['Company Type'] ??
    filterValues?.['Company Type'] ??
    null;
  const companySelection = {
    ...(companyTypeFilterValue ? { 'company-type': String(companyTypeFilterValue) } : {}),
    ...(companyFilterValue ? { company: String(companyFilterValue) } : {}),
  };
  const companyDisplayName = (() => {
    if (!companyFilterValue) return null;
    if (narrativeFilterTree?.filterHierarchy?.length) {
      return findNodeLabel(narrativeFilterTree, companySelection, 'company') || String(companyFilterValue);
    }
    return String(companyFilterValue);
  })();
  const companyDescription = (() => {
    if (!companyFilterValue || !narrativeFilterTree?.filterHierarchy?.length) return null;
    const description = findNodeDescription(narrativeFilterTree, companySelection, 'company');
    if (!description) return null;
    if (companyDisplayName && description.toLowerCase() === String(companyDisplayName).toLowerCase()) {
      return null;
    }
    return description;
  })();

  const asset = filterValues?.Asset ?? filterValues?.Narrative ?? companyDisplayName ?? null;

  const narrativeInfo = (() => {
    if (!asset) return { displayAsset: null, description: null };

    const narrativeFilter = filters?.find((f: { id: string }) => f?.id === 'Narrative');
    const matchingOption = narrativeFilter?.options?.find(
      (opt: { name: any; id?: any }) => opt?.name === asset || opt?.id === asset,
    );

    // Use name instead of id for display if asset matches the id
    let displayAsset = asset;
    if (matchingOption && asset === matchingOption.id) {
      displayAsset = matchingOption.name;
    }

    const rawDesc = matchingOption?.description;

    // Final description logic
    let description = rawDesc;

    if (
      !description ||
      description === '' ||
      description === asset ||
      (typeof description === 'string' &&
        typeof displayAsset === 'string' &&
        description.toLowerCase() === displayAsset.toLowerCase())
    ) {
      description = null;
    }

    return { displayAsset, description };
  })();

  const displayAsset = narrativeInfo.displayAsset;
  const titleDescription = (titleText.includes('{Company}') ? companyDescription : null) || narrativeInfo.description;

  const splitParam = titleText.includes('{Company}')
    ? '{Company}'
    : titleText.includes('{Narrative}') || titleDescription !== null
      ? '{Narrative}'
      : '{Asset}';

  const displayStocksTitleAssetName =
    splitParam === '{Asset}'
      ? searchData?.find((sd: any) => sd.category === 'Stocks')?.data?.find((item: any) => item?.label === asset)?.name
      : undefined;

  const displayTitleAssetName = displayStocksTitleAssetName;
  return (
    <WidgetOverridesContext.Provider value={widgetOverrides}>
      <ScrollWrap>
        <MainContainer id="maincontainer">
          <Header>
            {refreshFilterBar ? (
              <div />
            ) : (
              <FilterBar
                type={type}
                dateType={dateType}
                filters={filtersForBar}
                values={filterValues}
                initialSorting={initialSorting}
                onDownload={onDownload}
                tooltipCfg={tooltipCfg}
                setParentOpen={handleSetOpen}
                onSortingChange={onSortingChange}
                showSort={!!gridElements.find((ge: any) => ge.item.type === 'heatmap')}
                prismId={prismId}
                showDownloadAllButton={navHirarchyItem.showDownloadButton}
                onNarrativeSelectionChange={handleNarrativeSelectionChange}
                filterHierarchy={narrativeFilterTree}
                filterHierarchyValues={filterValuesID}
              />
            )}
          </Header>
          {isShowTitle && (
            <TitleBox>
              <TitleBoxStart>{navHirarchyItem.title?.split(splitParam)[0]}</TitleBoxStart>
              {displayAsset && !displayTitleAssetName && (
                <TitleBoxAssetName style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {displayAsset}
                  {titleDescription && (
                    <span
                      style={{
                        fontWeight: 'normal',
                        fontSize: '0.75em',
                      }}
                    >
                      {titleDescription}
                    </span>
                  )}
                </TitleBoxAssetName>
              )}
              {asset && displayTitleAssetName && <TitleBoxAsset>{`${displayTitleAssetName} (${asset})`}</TitleBoxAsset>}
              <TitleBoxEnd>{navHirarchyItem.title?.split(splitParam)[1]}</TitleBoxEnd>
            </TitleBox>
          )}

          <ContentContainer>
            <Container id="container" $hasChips={hasNarrativeChips}>
              <GridLayout
                className="layout"
                layout={baseLayoutArray}
                cols={columns}
                rowHeight={rowHeightPx}
                width={width - 16}
                margin={[gridHorizontalGap, gridVerticalGap]}
                isDraggable={false}
              >
                {children}
              </GridLayout>
            </Container>
          </ContentContainer>
        </MainContainer>
        <IndicatorsContainer ref={scrollerRef}>
          {indicatorSections &&
            gridFilterValues &&
            indicatorSections?.map((section: any, index: number) => (
              <IndicatorContainer
                key={index}
                $iswide={
                  windowWidth < 1200 || (indicatorSections.length === index + 1 && indicatorSections.length % 2 !== 0)
                    ? 1
                    : 0
                }
              >
                {section.type === 'bar' && (
                  <IndicatorStackedAreaChart
                    key={section.indicator + section.topic + section.symbol + String(index)}
                    {...{
                      category: gridCategory,
                      prism: gridPrism,
                      prismId: gridPrismId || 'BasketsHeatMap',
                      filterValues: gridFilterValues,
                      tooltipCfg,
                      ...section,
                    }}
                  ></IndicatorStackedAreaChart>
                )}
                {section.type === 'line' && (
                  <IndicatorLineChart
                    key={section.indicator + section.topic + section.symbol + String(index)}
                    {...{
                      category: gridCategory,
                      prism: gridPrism,
                      prismId: gridPrismId || 'BasketsHeatMap',
                      filterValues: gridFilterValues,
                      tooltipCfg,
                      ...section,
                    }}
                  ></IndicatorLineChart>
                )}
                {section.type === 'double-y-axis' && (
                  <IndicatorDoubleLineChart
                    key={section.indicator + section.topic + section.symbol + String(index)}
                    {...{
                      category: gridCategory,
                      prism: gridPrism,
                      prismId: gridPrismId || 'BasketsHeatMap',
                      filterValues: gridFilterValues,
                      tooltipCfg,
                      ...section,
                    }}
                  ></IndicatorDoubleLineChart>
                )}
                {section.type === 'table' && (
                  <IndicatorTable
                    key={section.indicator + section.topic + section.symbol + String(index)}
                    {...{
                      category: gridCategory,
                      prism: gridPrism,
                      prismId: gridPrismId || 'BasketsHeatMap',
                      filterValues: gridFilterValues,
                      tooltipCfg,
                      ...section,
                    }}
                  ></IndicatorTable>
                )}
              </IndicatorContainer>
            ))}
        </IndicatorsContainer>
        <FabContainer>
          {offset > 10 && (
            <Fab onClick={onFabClick}>
              <ExpandLess />
            </Fab>
          )}
        </FabContainer>
      </ScrollWrap>
    </WidgetOverridesContext.Provider>
  );
}
